// ============================================================================
// budgetVendorService — Vendor Directory Layer
// ----------------------------------------------------------------------------
// Manages external budget vendors scoped by workspaceId.
// ============================================================================

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { isNoSignInPreviewActive } from '../debug'
import type { FieldSection } from '../components/Common/EntityFormDialog'

export const BUDGET_VENDORS_COLLECTION = 'budget_vendors'

export interface BudgetVendor {
  id: string
  workspaceId: string
  name: string
  contactEmail: string | null
  taxId: string | null
  notes: string | null
  createdAt: Timestamp | null
}

export const VENDOR_FORM_SECTIONS: FieldSection[] = [
  {
    id: 'basics',
    label: 'Basics',
    fields: [
      { id: 'name', label: 'Vendor name', type: 'text', required: true, placeholder: 'e.g. Acme Services' },
      { id: 'contactEmail', label: 'Contact email', type: 'text', placeholder: 'billing@example.com' },
      { id: 'taxId', label: 'Tax ID / VAT No.', type: 'text', placeholder: 'e.g. US123456789' },
    ],
  },
  {
    id: 'notes',
    label: 'Notes',
    fields: [
      { id: 'notes', label: 'Vendor notes', type: 'textarea', placeholder: 'Payment terms, banking details, notes...' },
    ],
  },
]

const toBudgetVendor = (id: string, data: DocumentData): BudgetVendor => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  name: String(data.name ?? ''),
  contactEmail: data.contactEmail != null ? String(data.contactEmail) : null,
  taxId: data.taxId != null ? String(data.taxId) : null,
  notes: data.notes != null ? String(data.notes) : null,
  createdAt: (data.createdAt as Timestamp | null) ?? null,
})

export interface CreateBudgetVendorInput {
  workspaceId: string
  name: string
  contactEmail?: string | null
  taxId?: string | null
  notes?: string | null
}

export const subscribeBudgetVendors = (
  workspaceId: string,
  onData: (vendors: BudgetVendor[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onData([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    onData([])
    return () => {}
  }

  const q = query(
    collection(db, BUDGET_VENDORS_COLLECTION),
    where('workspaceId', '==', workspaceId),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) =>
        toBudgetVendor(docSnap.id, docSnap.data()),
      )
      onData(items.sort((a, b) => a.name.localeCompare(b.name)))
    },
    (error) => {
      console.warn('[budgetVendorService] subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

export const createBudgetVendor = async (
  input: CreateBudgetVendorInput,
  db: Firestore = firestoreDb,
): Promise<string> => {
  if (!input.workspaceId) throw new Error('workspaceId is required')
  if (!input.name?.trim()) throw new Error('Vendor name is required')

  const docRef = doc(collection(db, BUDGET_VENDORS_COLLECTION))
  await setDoc(docRef, {
    workspaceId: input.workspaceId,
    name: input.name.trim(),
    contactEmail: input.contactEmail ? input.contactEmail.trim() : null,
    taxId: input.taxId ? input.taxId.trim() : null,
    notes: input.notes ? input.notes.trim() : null,
    createdAt: serverTimestamp(),
  })
  return docRef.id
}

export const updateBudgetVendor = async (
  id: string,
  patch: Partial<CreateBudgetVendorInput>,
  db: Firestore = firestoreDb,
): Promise<void> => {
  if (!id) throw new Error('Vendor ID is required')
  const docRef = doc(db, BUDGET_VENDORS_COLLECTION, id)
  const clean: Record<string, unknown> = {}
  if (patch.name !== undefined) clean.name = patch.name?.trim() ?? ''
  if (patch.contactEmail !== undefined) clean.contactEmail = patch.contactEmail ? patch.contactEmail.trim() : null
  if (patch.taxId !== undefined) clean.taxId = patch.taxId ? patch.taxId.trim() : null
  if (patch.notes !== undefined) clean.notes = patch.notes ? patch.notes.trim() : null

  await updateDoc(docRef, clean)
}

export const deleteBudgetVendor = async (
  id: string,
  db: Firestore = firestoreDb,
): Promise<void> => {
  if (!id) throw new Error('Vendor ID is required')
  const docRef = doc(db, BUDGET_VENDORS_COLLECTION, id)
  await deleteDoc(docRef)
}
